import { useEffect, useState } from 'react';

import type { ResponseData } from './useAlertBannerData';
import { useAlertBannerData } from './useAlertBannerData';

import * as AnnualBillingUpsell from '../banners/AnnualBillingUpsell';
import * as BannerMessage from '../banners/BannerMessage';
import * as CompaniesTrialEnded from '../banners/CompaniesTrialEnded';
import * as CompaniesTrialing from '../banners/CompaniesTrialing';
import * as EnterpriseTrialEnded from '../banners/EnterpriseTrialEnded';
import * as EnterpriseTrialing from '../banners/EnterpriseTrialing';
import * as ProfessionalTrialEnded from '../banners/ProfessionalTrialEnded';
import * as ProfessionalTrialing from '../banners/ProfessionalTrialing';
import * as RefreshRequired from '../banners/RefreshRequired';
import * as TeamsDowngraded from '../banners/TeamsDowngraded';
import * as TeamsTrialEnded from '../banners/TeamsTrialEnded';
import * as OverdueInvoice from '../banners/OverdueInvoice';
import * as TeamsTrialing from '../banners/TeamsTrialing';
import * as TaskUsage from '../banners/TaskUsage';
import { BannerProps } from '../banners/types';
import * as UnlimitedAccount from '../banners/UnlimitedAccount';
import * as AnnualPlanRenewalNotice from '../banners/AnnualPlanRenewalNotice';

type UseAlertReturn = {
  isLoading: boolean;
  data?: ResponseData;
  Component?: (props: BannerProps) => JSX.Element | null;
};

const possibleComponents = [
  RefreshRequired,
  TaskUsage,
  BannerMessage,
  UnlimitedAccount,
  CompaniesTrialing,
  CompaniesTrialEnded,
  EnterpriseTrialing,
  EnterpriseTrialEnded,
  TeamsTrialing,
  TeamsTrialEnded,
  OverdueInvoice,
  AnnualPlanRenewalNotice,
  TeamsDowngraded,
  ProfessionalTrialing,
  ProfessionalTrialEnded,
  AnnualBillingUpsell,
];

export const useAlertBanner = (): UseAlertReturn => {
  const [lastRenderedTime, setLastRenderedTime] = useState(Date.now());
  const [isExcludedUrl, setIsExcludedUrl] = useState(false);

  // Rerender every few minutes to ensure data is up to date. This is
  // especially important for `RefreshRequired`.
  useEffect(() => {
    setTimeout(
      () => {
        setLastRenderedTime(Date.now());
      },
      5 * 60 * 60 * 1000
    );
  }, [lastRenderedTime]);

  useEffect(() => {
    setIsExcludedUrl(window.location.pathname.includes('team-account-setup'));
  }, []);

  const { isLoading, isError, data } = useAlertBannerData();

  const Component =
    isLoading || isError || !data
      ? undefined
      : possibleComponents.find((component) => {
          return component.shouldRender(data, isExcludedUrl);
        })?.Component;
  return {
    data,
    Component,
    isLoading,
  };
};
